import {Nullable} from "../../models/common/Nullable";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import { ofType, StateObservable } from "redux-observable";
import { AppState } from "../../reducers/RootReducer";
import { switchMap, map, catchError } from "rxjs/operators";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {
    ProjectCommissionStateModule,
    ProjectCommissionState,
    ProjectCommissionStateId, ProjectCommissionStateRequest, ProjectCommissionStateRequestProp
} from "../../models/projects/ProjectCommissionState";
import { ajax } from "rxjs/ajax";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import { Observable, of } from 'rxjs';
import {Identifier} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";


/** backend api path */
function apiPath( id: Nullable< number > = null ): string {
    if( id === null ) {
        return APP_CONFIG.baseWeb + 'projects/commission_states/';
    } else {
        return `${ APP_CONFIG.baseWeb }projects/commission_states/${ id }/`;
    }
}

/**
 * all ProjectCommissionState action types union
 */
export type ProjectCommissionStateActionTypes = FetchProjectCommissionStateActionTypes
    | FetchProjectCommissionStateByIdActionTypes
    | CreateProjectCommissionStateActionTypes
    | UpdateProjectCommissionStateActionTypes
    | DeleteProjectCommissionStateActionTypes
    | RestoreProjectCommissionStateActionTypes


/**
 * FETCH LIST
 */
export interface FetchProjectCommissionStateAction extends ReduxAction<typeof FETCH_PROJECT_COMMISSION_STATE>  {

}
export interface FetchProjectCommissionStateSuccessAction extends ReduxAction<typeof FETCH_PROJECT_COMMISSION_STATE_SUCCESS> {
    payload: ApiReturnType< ProjectCommissionState[] >
}
export interface FetchProjectCommissionStateErrorAction extends ErrorAction<typeof FETCH_PROJECT_COMMISSION_STATE_ERROR> {

}

/** all fetch types union */
export type FetchProjectCommissionStateActionTypes = FetchProjectCommissionStateAction
    | FetchProjectCommissionStateSuccessAction
    | FetchProjectCommissionStateErrorAction

/**
 * redux action constants
 */
export const FETCH_PROJECT_COMMISSION_STATE = "FETCH_PROJECT_COMMISSION_STATE";
export const FETCH_PROJECT_COMMISSION_STATE_SUCCESS = "FETCH_PROJECT_COMMISSION_STATE_SUCCESS";
export const FETCH_PROJECT_COMMISSION_STATE_ERROR = "FETCH_PROJECT_COMMISSION_STATE_ERROR";

/** redux action function */
export function fetchProjectCommissionState(): FetchProjectCommissionStateAction {
    return { type: FETCH_PROJECT_COMMISSION_STATE };
}
export function fetchProjectCommissionStateSuccess( payload: ApiReturnType<ProjectCommissionState[]> ): FetchProjectCommissionStateSuccessAction {
    return { type: FETCH_PROJECT_COMMISSION_STATE_SUCCESS, payload };
}
export function fetchProjectCommissionStateError( error: any ): FetchProjectCommissionStateErrorAction {
    return { type: FETCH_PROJECT_COMMISSION_STATE_ERROR, error };
}

/** redux observable epic */
export function fetchProjectCommissionStateEpic(
    action$: Observable< FetchProjectCommissionStateAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_COMMISSION_STATE ),
        switchMap( _ => {
            const apiRequest = getUserViewSettingsApiRequest(
                apiPath(),
                ProjectCommissionStateModule.identifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject( apiRequest.path ) ).pipe(
                map( response => fetchProjectCommissionStateSuccess(
                    response.response as ApiReturnType< ProjectCommissionState[] >
                ) ),
                catchError( error => of(
                    fetchProjectCommissionStateError( error ),
                    eventNotificationError( error, store$, ProjectCommissionStateModule.identifier, "fetchCommissionState" )
                ) )
            );
        })
    );
}

/**
 * FETCH SINGLE ENTRY
 */
export interface FetchProjectCommissionStateByIdAction extends ProjectCommissionStateId, ReduxAction<typeof FETCH_PROJECT_COMMISSION_STATE_BY_ID> {

}
export interface FetchProjectCommissionStateByIdSuccessAction extends ReduxAction<typeof FETCH_PROJECT_COMMISSION_STATE_BY_ID_SUCCESS> {
    payload: ProjectCommissionState
}
export interface FetchProjectCommissionStateByIdErrorAction extends ProjectCommissionStateId, ErrorAction<typeof FETCH_PROJECT_COMMISSION_STATE_BY_ID_ERROR> {

}

/** action types union */
export type FetchProjectCommissionStateByIdActionTypes = FetchProjectCommissionStateByIdAction
    | FetchProjectCommissionStateByIdSuccessAction
    | FetchProjectCommissionStateByIdErrorAction

/** REDUX ACTION CONSTANTS */
export const FETCH_PROJECT_COMMISSION_STATE_BY_ID = "FETCH_PROJECT_COMMISSION_STATE_BY_ID";
export const FETCH_PROJECT_COMMISSION_STATE_BY_ID_SUCCESS = "FETCH_PROJECT_COMMISSION_STATE_BY_ID_SUCCESS";
export const FETCH_PROJECT_COMMISSION_STATE_BY_ID_ERROR = "FETCH_PROJECT_COMMISSION_STATE_BY_ID_ERROR";

/** REDUX ACTION functions */
export function fetchProjectCommissionStateById( stateId: Identifier<number> ): FetchProjectCommissionStateByIdAction {
    return { type: FETCH_PROJECT_COMMISSION_STATE_BY_ID, stateId };
}
export function fetchProjectCommissionStateByIdSuccess( payload: ProjectCommissionState ): FetchProjectCommissionStateByIdSuccessAction {
    return { type: FETCH_PROJECT_COMMISSION_STATE_BY_ID_SUCCESS, payload };
}
export function fetchProjectCommissionStateByIdError( stateId: Identifier<number>, error: any ): FetchProjectCommissionStateByIdErrorAction {
    return { type: FETCH_PROJECT_COMMISSION_STATE_BY_ID_ERROR, error, stateId };
}

/** redux observable epic */
export function fetchProjectCommissionStateByIdEpic(
    action$: Observable< FetchProjectCommissionStateByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_COMMISSION_STATE_BY_ID ),
        switchMap( action => {

            return ajax( APP_CONFIG.getRequestObject( apiPath( action.stateId.rawValue ) ) ).pipe(
                map( response => fetchProjectCommissionStateByIdSuccess( response.response as ProjectCommissionState ) ),
                catchError( error => of(
                    fetchProjectCommissionStateByIdError( action.stateId, error ),
                    eventNotificationError( error, store$, ProjectCommissionStateModule.identifier, "fetchById" )
                ) )
            );
        })
    )
}

/**
 * CREATE PROJECT COMMISSION STATE
 */
export interface CreateProjectCommissionStateAction extends ProjectCommissionStateRequestProp, ReduxAction<typeof CREATE_PROJECT_COMMISSION_STATE> {
}
export interface CreateProjectCommissionStateSuccessAction extends ProjectCommissionStateRequestProp, ReduxAction<typeof CREATE_PROJECT_COMMISSION_STATE_SUCCESS> {
}
export interface CreateProjectCommissionStateErrorAction extends ErrorAction<typeof CREATE_PROJECT_COMMISSION_STATE_ERROR> {

}

/** create action types union */
export type CreateProjectCommissionStateActionTypes = CreateProjectCommissionStateAction
    | CreateProjectCommissionStateSuccessAction
    | CreateProjectCommissionStateErrorAction

/** create redux action constants */
export const CREATE_PROJECT_COMMISSION_STATE = "CREATE_PROJECT_COMMISSION_STATE";
export const CREATE_PROJECT_COMMISSION_STATE_SUCCESS = "CREATE_PROJECT_COMMISSION_STATE_SUCCESS";
export const CREATE_PROJECT_COMMISSION_STATE_ERROR = "CREATE_PROJECT_COMMISSION_STATE_ERROR";

/** create redux functions */
export function createProjectCommissionState( projectCommissionStateReq: ProjectCommissionStateRequest ): CreateProjectCommissionStateAction {
    return { type: CREATE_PROJECT_COMMISSION_STATE, projectCommissionStateReq };
}
export function createProjectCommissionStateSuccess( projectCommissionStateReq: ProjectCommissionStateRequest  ): CreateProjectCommissionStateSuccessAction {
    return { type: CREATE_PROJECT_COMMISSION_STATE_SUCCESS, projectCommissionStateReq };
}
export function createProjectCommissionStateError( error: any ): CreateProjectCommissionStateErrorAction {
    return { type: CREATE_PROJECT_COMMISSION_STATE_ERROR, error };
}

/** redux observable epic */
export function createProjectCommissionStateEpic(
    action$: Observable< CreateProjectCommissionStateAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT_COMMISSION_STATE ),
        switchMap( action => {

            return ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    action.projectCommissionStateReq
                )
            ).pipe(
                switchMap( _ => of(
                    createProjectCommissionStateSuccess( action.projectCommissionStateReq ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.projectCommissionState.routes.main.path,
                        ProjectCommissionStateModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createProjectCommissionStateError( error ),
                    eventNotificationError( error, store$, ProjectCommissionStateModule.identifier, "create" )
                ) )
            )
        })
    );
}

/**
 * UPDATE ProjectCommissionState
 */
export interface UpdateProjectCommissionStateAction extends ProjectCommissionStateRequestProp, ReduxAction<typeof UPDATE_PROJECT_COMMISSION_STATE> {}
export interface UpdateProjectCommissionStateSuccessAction extends ProjectCommissionStateRequestProp, ReduxAction<typeof UPDATE_PROJECT_COMMISSION_STATE_SUCCESS> {}
export interface UpdateProjectCommissionStateErrorAction extends ErrorAction<typeof UPDATE_PROJECT_COMMISSION_STATE_ERROR> {}

/** redux action types union */
export type UpdateProjectCommissionStateActionTypes = UpdateProjectCommissionStateAction
    | UpdateProjectCommissionStateSuccessAction
    | UpdateProjectCommissionStateErrorAction

/** redux action constants */
export const UPDATE_PROJECT_COMMISSION_STATE = "UPDATE_PROJECT_COMMISSION_STATE";
export const UPDATE_PROJECT_COMMISSION_STATE_SUCCESS = "UPDATE_PROJECT_COMMISSION_STATE_SUCCESS";
export const UPDATE_PROJECT_COMMISSION_STATE_ERROR = "UPDATE_PROJECT_COMMISSION_STATE_ERROR";

/** REDUX ACTION FUNCTIONS*/
export function updateProjectCommissionState( projectCommissionStateReq: ProjectCommissionStateRequest ): UpdateProjectCommissionStateAction {
    return { type: UPDATE_PROJECT_COMMISSION_STATE, projectCommissionStateReq };
}
export function updateProjectCommissionStateSuccess( projectCommissionStateReq: ProjectCommissionStateRequest ): UpdateProjectCommissionStateSuccessAction {
    return { type: UPDATE_PROJECT_COMMISSION_STATE_SUCCESS, projectCommissionStateReq };
}
export function updateProjectCommissionStateError( error: any ): UpdateProjectCommissionStateErrorAction {
    return { type: UPDATE_PROJECT_COMMISSION_STATE_ERROR, error };
}

/** redux observable epic */
export function updateProjectCommissionStateEpic(
    action$: Observable< UpdateProjectCommissionStateAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( UPDATE_PROJECT_COMMISSION_STATE ),
        switchMap( action => {
            return ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.put,
                    action.projectCommissionStateReq
                )
            ).pipe(
                    switchMap( _ => of(
                        updateProjectCommissionStateSuccess( action.projectCommissionStateReq ),
                        eventNotificationSuccess(),
                        sendUserToPage(
                            RouteConfig.projectCommissionState.routes.main.path,
                            ProjectCommissionStateModule.identifier
                        )
                    ) ),
                    catchError( error => of(
                        updateProjectCommissionStateError( error ),
                        eventNotificationError(error, store$, ProjectCommissionStateModule.identifier, "update")
                    ) )
            );
        } )
    );
}

/**
 * DELETE ProjectCommissionState
 */
export interface DeleteProjectCommissionStateAction extends ProjectCommissionStateId, ReduxAction<typeof DELETE_PROJECT_COMMISSION_STATE> {}
export interface DeleteProjectCommissionStateSuccessAction extends ProjectCommissionStateId, ReduxAction<typeof DELETE_PROJECT_COMMISSION_STATE_SUCCESS> {}
export interface DeleteProjectCommissionStateErrorAction extends ErrorAction<typeof DELETE_PROJECT_COMMISSION_STATE_ERROR> {}

/** redux action types union */
export type DeleteProjectCommissionStateActionTypes = DeleteProjectCommissionStateAction
    | DeleteProjectCommissionStateSuccessAction
    | DeleteProjectCommissionStateErrorAction

/** redux action constants */
export const DELETE_PROJECT_COMMISSION_STATE = "DELETE_PROJECT_COMMISSION_STATE";
export const DELETE_PROJECT_COMMISSION_STATE_SUCCESS = "DELETE_PROJECT_COMMISSION_STATE_SUCCESS";
export const DELETE_PROJECT_COMMISSION_STATE_ERROR = "DELETE_PROJECT_COMMISSION_STATE_ERROR";

/** REDUX ACTION FUNCTIONS*/
export function deleteProjectCommissionState( stateId: Identifier<number> ): DeleteProjectCommissionStateAction {
    return { type: DELETE_PROJECT_COMMISSION_STATE, stateId };
}
export function deleteProjectCommissionStateSuccess( stateId: Identifier<number> ): DeleteProjectCommissionStateSuccessAction {
    return { type: DELETE_PROJECT_COMMISSION_STATE_SUCCESS, stateId };
}
export function deleteProjectCommissionStateError( error: any ): DeleteProjectCommissionStateErrorAction {
    return { type: DELETE_PROJECT_COMMISSION_STATE_ERROR, error };
}

/** redux observable epic */
export function deleteProjectCommissionStateEpic(
    action$: Observable< DeleteProjectCommissionStateAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( DELETE_PROJECT_COMMISSION_STATE ),
        switchMap( action => {
            return ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.stateId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteProjectCommissionStateSuccess( action.stateId ),
                    eventNotificationSuccess(),
                    fetchProjectCommissionStateById( action.stateId ),
                    sendUserToPage(
                        RouteConfig.projectCommissionState.routes.main.path,
                        ProjectCommissionStateModule.identifier
                    )
                ) ),
                catchError( error => of(
                    deleteProjectCommissionStateError( error ),
                    eventNotificationError(error, store$, ProjectCommissionStateModule.identifier, "delete")
                ) )
            );
        } )
    );
}

/**
 * RESTORE ProjectCommissionState
 */
export interface RestoreProjectCommissionStateAction extends ProjectCommissionStateId, ReduxAction<typeof RESTORE_PROJECT_COMMISSION_STATE> {}
export interface RestoreProjectCommissionStateSuccessAction extends ProjectCommissionStateId, ReduxAction<typeof RESTORE_PROJECT_COMMISSION_STATE_SUCCESS> {}
export interface RestoreProjectCommissionStateErrorAction extends ErrorAction<typeof RESTORE_PROJECT_COMMISSION_STATE_ERROR> {}

/** redux action types union */
export type RestoreProjectCommissionStateActionTypes = RestoreProjectCommissionStateAction
    | RestoreProjectCommissionStateSuccessAction
    | RestoreProjectCommissionStateErrorAction

/** redux action constants */
export const RESTORE_PROJECT_COMMISSION_STATE = "RESTORE_PROJECT_COMMISSION_STATE";
export const RESTORE_PROJECT_COMMISSION_STATE_SUCCESS = "RESTORE_PROJECT_COMMISSION_STATE_SUCCESS";
export const RESTORE_PROJECT_COMMISSION_STATE_ERROR = "RESTORE_PROJECT_COMMISSION_STATE_ERROR";

/** REDUX ACTION FUNCTIONS*/
export function restoreProjectCommissionState( stateId: Identifier<number> ): RestoreProjectCommissionStateAction {
    return { type: RESTORE_PROJECT_COMMISSION_STATE, stateId };
}
export function restoreProjectCommissionStateSuccess( stateId: Identifier<number> ): RestoreProjectCommissionStateSuccessAction {
    return { type: RESTORE_PROJECT_COMMISSION_STATE_SUCCESS, stateId };
}
export function restoreProjectCommissionStateError( error: any ): RestoreProjectCommissionStateErrorAction {
    return { type: RESTORE_PROJECT_COMMISSION_STATE_ERROR, error };
}

/** redux observable epic */
export function restoreProjectCommissionStateEpic(
    action$: Observable< RestoreProjectCommissionStateAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( RESTORE_PROJECT_COMMISSION_STATE ),
        switchMap( action => {
            return ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.stateId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreProjectCommissionStateSuccess( action.stateId ),
                    eventNotificationSuccess(),
                    fetchProjectCommissionStateById( action.stateId ),
                    sendUserToPage(
                        RouteConfig.projectCommissionState.routes.main.path,
                        ProjectCommissionStateModule.identifier
                    )
                ) ),
                catchError( error => of(
                    restoreProjectCommissionStateError( error ),
                    eventNotificationError(error, store$, ProjectCommissionStateModule.identifier, "restore")
                ) )
            );
        } )
    );
}